// Unified search results — groups + events + courses + practices, all
// filtered client-side by the same query. Uses the browse endpoints so
// every signed-in user sees the same catalog.
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../lib/api';
import { PageHead, Section, Spinner, ErrorNote, Empty, useAsync } from '../components/ui';
import { GroupCard, EventCard, CourseCardView, PracticeCardView } from '../components/cards';

function matchQ(q, ...fields) {
  const hay = fields.filter(Boolean).join(' ').toLowerCase();
  return hay.includes(q);
}

export default function SearchResultsPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const q = (params.get('q') || '').trim().toLowerCase();
  const { loading, error, data, reload } = useAsync(async () => {
    const [groups, events, courses, practices, lessons] = await Promise.all([
      api.browseGroups().catch(() => []),
      api.browseEvents().catch(() => []),
      api.learnLibrary().catch(() => []),
      api.listLibraryPractices().catch(() => []),
      api.browseLessons().catch(() => []),
    ]);
    return {
      groups: Array.isArray(groups) ? groups : [],
      events: Array.isArray(events) ? events : [],
      courses: Array.isArray(courses) ? courses : [],
      practices: Array.isArray(practices) ? practices : [],
      lessons: Array.isArray(lessons) ? lessons : [],
    };
  }, [q]);

  if (!q) {
    return (
      <div className="page">
        <PageHead title="Search" />
        <Section><Empty>Type a search query in the header.</Empty></Section>
      </div>
    );
  }
  if (loading) return <div className="page"><PageHead title={`Search: ${q}`} /><Spinner /></div>;
  if (error) return <div className="page"><PageHead title={`Search: ${q}`} /><ErrorNote error={error} onRetry={reload} /></div>;

  const groups = data.groups.filter((g) => matchQ(q, g.name, g.description, g.location));
  const events = data.events.filter((e) => matchQ(q, e.requestName, e.topic, e.description, e.groupName, e.location));
  const courses = data.courses.filter((c) => matchQ(q, c.title, c.subtitle));
  const practices = data.practices.filter((p) => matchQ(q, p.title, p.subtitle));
  const lessons = data.lessons.filter((l) => matchQ(q, l.title, l.subtitle, l.courseTitle));

  const empty = groups.length === 0 && events.length === 0 && courses.length === 0 && practices.length === 0 && lessons.length === 0;

  return (
    <div className="page">
      <PageHead title={`Search: ${q}`} sub={empty ? 'No results' : null} />
      {empty ? (
        <Section><Empty>No matches for “{q}”.</Empty></Section>
      ) : (
        <>
          {courses.length > 0 && (
            <Section title={`Courses (${courses.length})`}>
              <div className="card-grid">{courses.map((c) => <CourseCardView key={c.id} c={c} />)}</div>
            </Section>
          )}
          {practices.length > 0 && (
            <Section title={`Practices (${practices.length})`}>
              <div className="card-grid">{practices.map((p) => <PracticeCardView key={p.id} p={p} />)}</div>
            </Section>
          )}
          {lessons.length > 0 && (
            <Section title={`Lessons (${lessons.length})`}>
              <div className="card-grid">
                {lessons.map((l) => (
                  <button key={l.id} className="item-card"
                    onClick={() => navigate(`/learn/lesson/${l.id}`)}>
                    <div className="item-title">📖 {l.title}</div>
                    {l.subtitle ? <div className="item-body">{l.subtitle}</div> : null}
                    <div className="item-chip">in {l.courseTitle}</div>
                  </button>
                ))}
              </div>
            </Section>
          )}
          {groups.length > 0 && (
            <Section title={`Groups (${groups.length})`}>
              <div className="card-grid">{groups.map((g) => <GroupCard key={g.id} group={g} />)}</div>
            </Section>
          )}
          {events.length > 0 && (
            <Section title={`Events (${events.length})`}>
              <div className="card-grid">{events.map((e) => <EventCard key={e.id} event={e} />)}</div>
            </Section>
          )}
        </>
      )}
    </div>
  );
}
